import React, { useEffect, useRef, useState } from "react";
import { isAxiosError, HttpStatusCode } from "axios";
import { ComponentProps } from "props";
import {
  IGroupStep,
  IProtocolStep,
  IProtocolStepKind,
  IProtocolStepStatus,
  TProtocolId,
  TProtocolStatus,
  TProtocolStepId,
} from "../types/protocol";
import { DeepPartial } from "@/utils/types/DeepPartial";
import { Box, Button, LoadingOverlay, Modal, Popover, Text, TextInput, TypographyStylesProvider } from "@mantine/core";
import protocolClasses from "./ProtocolRecorder.module.css"
import {
  IconArrowLeft,
  IconChevronDown,
  IconChevronUp,
  IconPlayerPlay,
  IconPlayerStop,
  IconTimezone,
  IconUser
} from "@tabler/icons-react";
import { StepsView } from "./steps/StepsView";
import { StepContentView } from "./steps/StepContentView";
import { FocusModeView } from "./FocusModeView";
import { useProtocolStore } from "../store/protocols";
import cx from "clsx";
import { useNeurotuneStore } from "@/common/store/neurotune.ts";
import { modals } from "@mantine/modals";
import { loadProtocolEvents, loadProtocolFiles, updateProtocol } from "../services/protocols"
import { TimezoneSelector } from "../../common/components/TimezoneSelector";
import { stopDataCollection as tauriStopDataCollection } from "@/common/services/tauriService";
import { useNavigate, useParams } from "react-router";
import { IEvent, TEventId, TSubjectId } from "@/common/types";
import { IProtocolStepEvent } from "@/protocols/types/IProtocolStepEvent.ts";
import { EventRecorder } from "@/common/components/EventRecorder.tsx";
import { EventListView } from "@/protocols/common/components/EventListView";
import dayjs, { Dayjs } from "dayjs";
import { useProtocolStepOrdering } from "./hooks/useProtocolStepOrdering";
import { useCountdownController, useStepNavigation } from "./hooks";
import {
  acquireProtocolWriteDrainer,
  releaseProtocolWriteDrainer,
  useWriteStatusStore,
  useUploadStatusStore,
  writeEvent as coordinatorWriteEvent,
  writeEventDelete as coordinatorWriteEventDelete,
  writeProtocol as coordinatorWriteProtocol,
  writeStep as coordinatorWriteStep,
} from "@/protocols/persistence";
import type { UploadSummary } from "@/protocols/persistence/uploadTypes";
import type { BrowserUploadProgress } from "@/protocols/persistence/useUploadStatusStore";
import { startReconnectAutoRetry } from "@/protocols/persistence/uploadCoordinator";
import { getWriteStore } from "@/protocols/persistence/writeStore";
import { getUploadStore } from "@/protocols/persistence/uploadStore";
import { PendingWritesTray } from "./components/PendingWritesTray";
import { PendingActivityModal } from "./components/PendingActivityModal";
import { FloatingCountdownPanel } from "./components/FloatingCountdownPanel";
import { whoAmI } from "@/common/services/userService.ts";
import {deriveGroupStatusFromStatuses} from "./utils/groupStatus";

export interface ProtocolRecorderProps extends ComponentProps {
}

type ExitIntent = 'navigate' | 'complete'

const SERVER_STATUS_PROBE_INTERVAL_MS = 5_000

// Mirrors PendingActivityModal's drain check: in-flight uploads
// (found/copied/pending/uploading) and any dead rows on either side
// block navigation. Persisted uploads are done and don't contribute.
// Browser-mode in-flight/failed browser uploads count too (browserInProgress)
// so the guard behaves the same in both modes.
// Returns a single integer so the three guard call sites can compare
// against `> 0` without recomputing the union.
function blockingNavigationTotal(
  pendingCount: number,
  deadCount: number,
  uploadSummary: UploadSummary,
  browserInProgress: number,
): number {
  const uploadInFlight =
    uploadSummary.found + uploadSummary.copied +
    uploadSummary.pending + uploadSummary.uploading
  return pendingCount + deadCount + uploadInFlight + uploadSummary.dead + browserInProgress
}

/** Browser uploads that are in flight or failed-but-retryable. */
function browserUploadActiveCount(
  browserUploads: Map<number, BrowserUploadProgress>,
): number {
  let n = 0
  browserUploads.forEach((e) => {
    if (e.status === 'in_flight' || e.status === 'dead') n += 1
  })
  return n
}

export const ProtocolRecorder: React.FC<ProtocolRecorderProps> = ({ }: ProtocolRecorderProps) => {

  const navigate = useNavigate()
  const protocolId = useParams().protocol_id as TProtocolId
  const subjectId = useParams().subject_id as TSubjectId
  const subject = useNeurotuneStore((state) => state.subjects.find((s) => s.id === subjectId))
  const activeCompany = useNeurotuneStore((state) => state.activeCompany)
  const loadProtocol = useProtocolStore((state) => state.loadProtocol)
  const protocols = useProtocolStore((state) => state.protocols)
  const timezone = useProtocolStore((state) => state.timezone)
  const setTimezone = useProtocolStore((state) => state.setTimezone)
  const [allProtocolEvents, setAllProtocolEvents] = useState<Array<IProtocolStepEvent>>([])
  const [protocolStepEvents, setProtocolStepEvents] = useState<Record<TProtocolStepId, Array<IEvent>>>({})
  const [protocolEvents, setProtocolEvents] = useState<Array<IEvent>>([])
  const [isEventRecorderExpanded, setIsEventRecorderExpanded] = useState(false);
  const eventRecorderRef = useRef<HTMLDivElement>(null);
  const [eventRecorderHeight, setEventRecorderHeight] = useState<number>(20);
  const [viewMode, setViewMode] = useState<'normal' | 'focus'>('normal')
  const [showDataCollectionExitModal, setShowDataCollectionExitModal] = useState(false)
  const [exitIntent, setExitIntent] = useState<ExitIntent>('navigate')
  const [isStoppingDataCollectionOnExit, setIsStoppingDataCollectionOnExit] = useState(false)
  const setServerFiles = useUploadStatusStore((s) => s.setServerFiles)
  const isDataCollectionRunning = useUploadStatusStore((s) => s.dataCollectionStatus?.watcherRunning ?? false)

  const selectedProtocol = protocols.find((p) => p.id === protocolId)

  const pendingCount = useWriteStatusStore(s => s.pendingCount)
  const deadCount = useWriteStatusStore(s => s.deadCount)
  const formResetEpoch = useWriteStatusStore(s => s.formResetEpoch)
  // Bootstrap may briefly set summary to null (e.g. when the
  // upload store hasn't been bootstrapped yet, or when the IPC
  // returns null in tests). Treat null as empty so the guard
  // doesn't crash and prevent the back button from rendering.
  const uploadSummary = useUploadStatusStore(s => s.summary) ?? {
    found: 0, copied: 0, pending: 0, uploading: 0, persisted: 0, dead: 0,
  }
  const browserInProgress = useUploadStatusStore(s => browserUploadActiveCount(s.browserUploads))

  // Use extracted hooks for step ordering and navigation
  const { orderedSteps, groupsByStepId } = useProtocolStepOrdering(selectedProtocol?.steps);

  const { activeStepIndex, setActiveStepIndex, onNext, onPrev, navigateToFirstIncomplete } = useStepNavigation({
    orderedSteps,
    onExitFocusMode: () => setViewMode('normal'),
  });

  const [sessionStartTime, setSessionStartTime] = useState<Dayjs | undefined>()
  const setOnline = useNeurotuneStore((s) => s.setOnline)

  const enterFocusMode = () => {
    setViewMode('focus');
    navigateToFirstIncomplete();
    if (!sessionStartTime) {
      setSessionStartTime(dayjs().tz(timezone!, false));
    }
  }
  useEffect(() => {
    const handleKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape' && viewMode === 'focus') {
        setViewMode('normal');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, onNext, onPrev]);

  // === Init: bootstrap → loadProtocol → drainer → upload worker (in that order) ===
  //
  // Order matters: bootstrap must complete before loadProtocol (so the
  // write-status store is hydrated); loadProtocol must complete before the
  // drainer starts (so its pending-writes merge sees all rows still in
  // 'pending' state — none claimed or persisted yet). The upload worker
  // starts AFTER the drainer so the tray pill reflects the post-init
  // state from the first 1 s poll.
  //
  // Runs the same way in Tauri and browser modes — the drainer is
  // backed by the WriteStore (TauriWriteStore or BrowserWriteStore).
  useEffect(() => {
    let cancelled = false
    const init = async () => {
      try {
        await useWriteStatusStore.getState().bootstrap(activeCompany.id, protocolId)
      } catch (e) {
        console.error('[init] bootstrap failed', e)
      }
      if (cancelled) return

      try {
        await useUploadStatusStore.getState().bootstrap(activeCompany.id, protocolId)
      } catch (e) {
        console.error('[init] upload bootstrap failed', e)
      }
      if (cancelled) return

      try {
        await loadProtocol(activeCompany.id, protocolId, true)
      } catch (e) {
        console.error('[init] loadProtocol failed', e)
      }
      if (cancelled) return

      const drainer = acquireProtocolWriteDrainer(activeCompany.id, protocolId)
      drainer.start()

      // Start the Rust upload worker so queued file uploads drain in the
      // background. The auth token is read from the neurotune_auth cookie
      // inside Tauri itself (see persistence/commands.rs::update_session_auth_token
      // for the recovery flow when the cookie is missing).
      try {
        await getUploadStore().startUploadWorker({
          companyId: activeCompany.id,
          protocolId,
          uploadRequestUrl: `${window.BIOS.neurotune_base_url}/api/companies/${activeCompany.id}/raw_files/ur`,
          subjectId,
          concurrency: 2,
        })
      } catch (e) {
        console.error('[init] startUploadWorker failed', e)
      }
    }
    void init()
    return () => {
      cancelled = true
      // Stop the upload worker BEFORE the write drainer so
      // the Rust worker doesn't outlive the protocol page
      // (the tray pill would otherwise flicker back to
      // "Uploading only" for a frame after the drainer is
      // gone).
      void getUploadStore().stopUploadWorker(activeCompany.id, protocolId).catch((e) => {
        console.error('[init] stopUploadWorker failed', e)
      })
      releaseProtocolWriteDrainer(activeCompany.id, protocolId)
      useWriteStatusStore.getState().shutdown()
      useUploadStatusStore.getState().shutdown()
    }
  }, [activeCompany.id, protocolId, subjectId])

  // === Mount-time event list ===
  //
  // Fetches real events from the server and merges any in-flight
  // pending writes so the user sees their unsaved events
  // immediately on initial mount — even before the drainer
  // has had a chance to persist them.
  //
  // Three sources are merged:
  //   1. Server events (canonical post-drain state, may include
  //      events the user queued deletes for before refresh).
  //   2. Pending event_create rows (optimistic entries with the
  //      client's canonical id; not yet on the server).
  //   3. Pending event_delete rows (coalesceKeys the drainer
  //      hasn't dispatched yet).
  //
  // Server events whose id matches a pending event_delete
  // coalesceKey are excluded — otherwise the user would briefly
  // see events they had already deleted (or scheduled to delete)
  // until the drainer dispatches the deletes. This was the
  // mount-time vs drainer race that caused stale state after
  // a refresh-then-server-restart.
  useEffect(() => {
    if (selectedProtocol) {
      const companyId = activeCompany.id
      const protocolIdStr = protocolId as unknown as string
      Promise.all([
        loadProtocolEvents(companyId, protocolId),
        getWriteStore().getPendingWrites({
          companyId: companyId as unknown as string,
          protocolId: protocolIdStr,
          stateFilter: ['pending', 'dead'],
          coalesceView: true,
        }),
      ]).then(([serverEvents, allEventRows]) => {
        // Split by writeType client-side. The single
        // fetch returns all pending+dead event rows
        // (creates and deletes) atomically, avoiding
        // the 4-call version's mount-time vs drainer
        // race where a cascade transition between two
        // fetches could lose rows.
        const createRows = allEventRows.filter((r) => r.writeType === 'event_create')
        const deleteRows = allEventRows.filter((r) => r.writeType === 'event_delete')
        const tempIdToOptimistic = new Map<string, IProtocolStepEvent>()
        for (const row of createRows) {
          const opt = row.payload as unknown as IProtocolStepEvent
          tempIdToOptimistic.set(opt.id as unknown as string, opt)
        }
        // Collect coalesceKeys of pending AND dead event_deletes so
        // server events targeted by them are filtered out of
        // the merged list. (Bootstrap auto-retries dead rows;
        // until then, the delete hasn't reached the server
        // so the server event is stale.)
        const pendingDeleteIds = new Set<string>()
        for (const row of deleteRows) {
          if (row.coalesceKey != null) {
            pendingDeleteIds.add(row.coalesceKey as unknown as string)
          }
        }
        const seenIds = new Set<string>()
        const merged: IProtocolStepEvent[] = []
        // Server events win on id collision (impossible for
        // client-generated tempIds, but defensive).
        for (const e of serverEvents) {
          const idStr = e.id as unknown as string
          if (pendingDeleteIds.has(idStr)) continue
          seenIds.add(idStr)
          merged.push(e)
        }
        for (const [tempId, opt] of tempIdToOptimistic) {
          if (!seenIds.has(tempId)) merged.push(opt)
        }
        setAllProtocolEvents(merged)
      }
      ).catch((e) => {
        console.error('[ProtocolRecorder] failed to load events', e)
      })
    }
  }, [activeCompany.id, selectedProtocol?.id]
  )

  // === Persist subscriber: handle event_delete (remove from local list) ===
  //
  // EventRecorder assigns the ids to new events before
  // enqueueing, so for event_create contains the correct server assigned
  // id  Both ProtocolStepEventId & EventId For event_delete, the local
  // event list may still contain the entry (e.g., loaded from
  // the server after a refresh, before the queued delete drains)
  // — we need to remove it once the delete is persisted so the
  // UI reflects the deletion without waiting for another
  // refresh.
  useEffect(() => {
    const companyId = activeCompany.id as unknown as string
    const protocolIdStr = protocolId as unknown as string
    const unsub = getWriteStore().onWritePersisted(
      companyId,
      protocolIdStr,
      (_rowId, coalesceKey, writeType) => {
        if (writeType !== 'event_delete') return
        if (coalesceKey == null) return
        setAllProtocolEvents((prev) => prev.filter((e) => e.id !== coalesceKey))
      },
    )
    return unsub
  }, [activeCompany.id, protocolId])

  useEffect(() => {
    const psEvents: Record<TProtocolStepId, [IEvent]> = {}
    const pEvents: Array<IEvent> = []
    allProtocolEvents.map((event) => {
      if (event.protocolStepId) {
        if (!psEvents[event.protocolStepId]) {
          psEvents[event.protocolStepId] = [event.event]
        } else {
          psEvents[event.protocolStepId].push(event.event)
        }
      } else {
        pEvents.push(event.event)
      }
    })
    setProtocolStepEvents(psEvents)
    setProtocolEvents(pEvents)
  }, [allProtocolEvents])

  useEffect(() => {
    const updateHeight = () => {
      if (eventRecorderRef.current) {
        const height = eventRecorderRef.current.offsetHeight;
        setEventRecorderHeight(height);
      }
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    if (eventRecorderRef.current) {
      observer.observe(eventRecorderRef.current);
    }

    return () => observer.disconnect();
  }, [isEventRecorderExpanded]);

  useEffect(() => {
    // Generation guard: probes overlap (the interval fires every 5 s
    // regardless of whether the previous probe settled), and a
    // slow-settling older probe must not overwrite a newer result —
    // e.g. a stale success resolving after a fresh failure would
    // flip the tray back to "Online" while actually offline.
    let isActive = false
    const probe = async () => {
      if (isActive) {
        return
      }
      isActive = true
      try {
        // Cache-bust: WKWebView/CDN caches could otherwise answer
        // a stale 200 while offline, masking the outage.
        await whoAmI({ retry: false, silent: true, params: { t: Date.now() } })
        setOnline(true)
      } catch (e) {
        // Auth failures (expired cookie) are NOT offline. Keep the
        // previous connectivity state and let the re-auth watcher
        // (apiClient -> reauthRedirect) drive the login redirect.
        // Flipping to Offline here previously masked expired
        // sessions and let users keep recording against a dead
        // cookie. Only genuine network failures flip the tray.
        const status = isAxiosError(e) ? e.response?.status : undefined
        if (status === HttpStatusCode.Unauthorized || status === HttpStatusCode.Forbidden) {
          return
        }
        if (status === undefined) {
          // Response-less failure (timeout / network / hung request). Logged
          // so ndc.log shows exactly how probes die when the tray flips
          // Offline without a server response.
          console.warn('[connectivity] probe failed without a response:', isAxiosError(e) ? e.code : String(e))
        }
        setOnline(false)
      } finally {
        isActive = false
      }
    }
    probe()
    const id = window.setInterval(probe, SERVER_STATUS_PROBE_INTERVAL_MS)
    return () => {
      window.clearInterval(id)
    }
  }, [])

  // Auto-retry failed browser uploads when connectivity returns.
  // Each dead entry keeps its File + args, so the retry re-runs the
  // whole upload (new presigned URL + PUT from byte 0). Wired to the
  // probe-driven isOnline flip (offline → online), not the window
  // 'online' event: navigator.onLine misses internet-down outages
  // and is unreliable in WKWebView, while the probe is authoritative.
  // Attempts are unlimited; at most one attempt per recovery. No-op
  // in Tauri mode (browserUploads is empty there).
  useEffect(() => {
    return startReconnectAutoRetry()
  }, [])

  // === Navigation guard: in-app + browser back ===
  //
  // Both back paths defer the actual navigation until the drainer has
  // caught up. The in-app button dispatches the existing drain-blocking
  // event with a navigate() callback; the browser back button is
  // intercepted via a sentinel history entry + popstate listener (Tauri
  // windows are still real browser windows and expose history APIs).
  const sentinelPushedRef = useRef(false)

  useEffect(() => {
    const handlePopState = () => {
      const { pendingCount: pc, deadCount: dc } = useWriteStatusStore.getState()
      const us = useUploadStatusStore.getState().summary ?? {
        found: 0, copied: 0, pending: 0, uploading: 0, persisted: 0, dead: 0,
      }
      const bc = browserUploadActiveCount(useUploadStatusStore.getState().browserUploads)
      if (blockingNavigationTotal(pc, dc, us, bc) > 0) {
        history.pushState(null, '', window.location.href)
        sentinelPushedRef.current = true
        window.dispatchEvent(new CustomEvent('show-pending-activity-modal', {
          detail: { mode: 'drain', onResolved: async () => history.back() },
        }))
      }
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  useEffect(() => {
    const total = blockingNavigationTotal(pendingCount, deadCount, uploadSummary, browserInProgress)
    if (total > 0 && !sentinelPushedRef.current) {
      history.pushState(null, '', window.location.href)
      sentinelPushedRef.current = true
    } else if (total === 0 && sentinelPushedRef.current) {
      sentinelPushedRef.current = false
      history.back()
    }
  }, [pendingCount, deadCount, uploadSummary, browserInProgress])


  // here we periodically refresh our view of server files.
  useEffect(() => {
    const refreshUploadedFiles = async () => {
      try {
        const files = await loadProtocolFiles(activeCompany.id, protocolId, { silent: true });
        setServerFiles(files);
      } catch (error) {
        console.error("Failed to refresh protocol files:", error);
      }
    };
    refreshUploadedFiles()
    const id = window.setInterval(refreshUploadedFiles, 1000)
    return () => {
      window.clearInterval(id)
    }
  }, [activeCompany.id, protocolId])

  const createEvent = async (
    eventData: IProtocolStepEvent,
  ): Promise<IProtocolStepEvent> => {
    // Single creation hook for EventRecorder. Routes through the
    // coordinator so the event is durable (localStorage/SQLite
    // queue), retried on transient failure, and surfaces in
    // PendingWritesTable on non-retryable failure. The event is
    // added to local state only once the write queue has accepted it
    // (durable-first: a rejected enqueue means nothing renders and
    // the coordinator's error notification is the surfacing).
    // EventRecorder has already assigned the
    // canonical id (same value on outer `id` and inner `event.id`),
    // so no tempId → server id translation is needed.
    const queued = await coordinatorWriteEvent(
      activeCompany.id, protocolId, eventData,
    )
    setAllProtocolEvents((prev) => [queued, ...prev])
    return queued
  }
  const onDeleteEvent = (eventId: TEventId) => {
    // Find the matching IProtocolStepEvent. We use the
    // IProtocolStepEvent.id (which equals the canonical id used
    // at create time) as the delete target — this is the same
    // id the server uses for the protocol_steps_events row and
    // for the URL path on DELETE /events/{id}.
    const event = allProtocolEvents.find((e) => e.event.id === eventId)
    if (!event) return
    // Durable-first: the entry is removed from the UI only once the
    // delete has been accepted by the write queue. On terminal enqueue
    // failure the coordinator rejects after surfacing the error, and
    // the event stays visible (nothing was queued, nothing is removed).
    void coordinatorWriteEventDelete(activeCompany.id, protocolId, event.id)
      .then(() => {
        setAllProtocolEvents((prev) => prev.filter((e) => e.event.id !== eventId))
      })
      .catch((e) => {
        // Enqueue failed terminally (the coordinator already notified)
        // or post-enqueue processing failed — either way keep the event
        // visible and leave a trace in the console.
        console.error('[ProtocolRecorder] event delete was not queued', e)
      })
  }

  const onStepChanged = (stepId: TProtocolStepId, stepUpdates: DeepPartial<IProtocolStep>) => {
    void coordinatorWriteStep(activeCompany.id, protocolId, stepId, stepUpdates)

    // Keep parent GROUP statuses in sync when a member status changes,
    // including updates triggered from focus mode where group views are hidden.
    const updatedStatus = stepUpdates.status
    if (!updatedStatus || !selectedProtocol) {
      return
    }

    const stepsById = new Map(selectedProtocol.steps.map((s) => [s.id, s]))
    const parentGroups = selectedProtocol.steps.filter(
      (s): s is IGroupStep =>
        s.kind === IProtocolStepKind.GROUP && (s as IGroupStep).memberStepIds.includes(stepId),
    )

    parentGroups.forEach((group) => {
      const memberStatuses = group.memberStepIds
        .map((memberStepId) => {
          if (memberStepId === stepId) {
            return updatedStatus
          }
          return stepsById.get(memberStepId)?.status
        })
        .filter((status): status is IProtocolStepStatus => status !== undefined)

      if (memberStatuses.length !== group.memberStepIds.length) {
        return
      }

      const groupStatus = deriveGroupStatusFromStatuses(memberStatuses)
      if (groupStatus !== group.status) {
        void coordinatorWriteStep(activeCompany.id, protocolId, group.id, {status: groupStatus})
      }
    })
  }

  const {
    countDownState,
    startCountdown,
    stopCountdown,
    cancelCountdown,
  } = useCountdownController({
    orderedSteps,
    timezone,
    onStepChange: onStepChanged,
  })

  const openProtocolInstructions = () => {
    modals.open({
      title: "Protocol Instructions",
      size: "large",
      radius: "md",
      centered: true,
      withCloseButton: true,
      children: (
        <div className={cx(protocolClasses.instructions)}>
          <TypographyStylesProvider>
            <div dangerouslySetInnerHTML={{ __html: selectedProtocol?.instructions || "No Instructions" }} />
          </TypographyStylesProvider>
        </div>
      )
    })
  }
  const renderNormalContent = () => {
    if (selectedProtocol && orderedSteps && orderedSteps.length > activeStepIndex) {
      const step = orderedSteps[activeStepIndex]
      const groupStep = groupsByStepId[step.id]

      return (
        <Box className={cx(protocolClasses.contentPanel)}>
          <StepContentView
            step={groupStep || step}
            subjectId={selectedProtocol.subjectId}
            allSteps={orderedSteps}
            onChange={onStepChanged}
            protocolStepEvents={protocolStepEvents}
            onDeleteEvent={onDeleteEvent}
            selectedStep={step}
            onStepSelected={onStepSelected}
            createEvent={createEvent}
          />
        </Box>
      )

    } else {
      return <></>
    }
  }

  const renderFocusContent = () => {
    if (selectedProtocol && orderedSteps && orderedSteps.length > activeStepIndex) {
      const step = orderedSteps[activeStepIndex]
      const prevStep = activeStepIndex > 0 ? orderedSteps[activeStepIndex - 1] : undefined
      const nextStep = activeStepIndex < orderedSteps.length - 1 ? orderedSteps[activeStepIndex + 1] : undefined

      return (
        <FocusModeView
          prevStep={prevStep}
          step={step}
          nextStep={nextStep}
          index={activeStepIndex}
          subjectId={selectedProtocol.subjectId}
          protocolStepEvents={protocolStepEvents}
          onDeleteEvent={onDeleteEvent}
          setViewMode={setViewMode}
          onNext={onNext}
          onPrev={onPrev}
          timezone={timezone}
          startTime={sessionStartTime}
          onStepChange={onStepChanged}
          orderedSteps={orderedSteps}
          createEvent={createEvent}
          countDownState={countDownState}
          onStartCountdown={(duration, description) => startCountdown(step, duration, description)}
          onStopCountdown={stopCountdown}
          onCancelCountdown={cancelCountdown}
          onJumpToIndex={(index) => {
            if (index < orderedSteps.length) {
              setActiveStepIndex(index);
            } else {
              setViewMode('normal');
            }
          }}
        />
      )

    } else {
      return <></>
    }
  }

  if (!selectedProtocol)
    return <LoadingOverlay visible={true} zIndex={1000} />

  const onClickBack = () => {
    const { pendingCount: pc, deadCount: dc } = useWriteStatusStore.getState()
    const us = useUploadStatusStore.getState().summary ?? {
      found: 0, copied: 0, pending: 0, uploading: 0, persisted: 0, dead: 0,
    }
    const bc = browserUploadActiveCount(useUploadStatusStore.getState().browserUploads)
    if (blockingNavigationTotal(pc, dc, us, bc) > 0) {
      window.dispatchEvent(new CustomEvent('show-pending-activity-modal', {
        detail: { mode: 'drain', onResolved: async () => navigate("/sessions/visit") },
      }))
      return
    }
    if (isDataCollectionRunning) {
      setExitIntent('navigate')
      setShowDataCollectionExitModal(true)
      return
    }
    navigate("/sessions/visit")
  }

  const onCompleted = () => {
    const completeAndNavigate = async () => {
      if (!selectedProtocol) return
      try {
        await updateProtocol(activeCompany.id as unknown as string, {
          ...selectedProtocol,
          status: TProtocolStatus.COMPLETED,
        }, { retry: false, silent: true })
        navigate("/sessions/visit")
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e)

        // update error message in PendingActivityModal
        window.dispatchEvent(new CustomEvent('set-pending-activity-complete-error', {
          detail: { message: msg },
        }))
        throw e
      }
    }
    window.dispatchEvent(new CustomEvent('show-pending-activity-modal', {
      detail: {
        mode: 'drain',
        intent: 'complete',
        disableAutoResolve: true,
        onResolved: completeAndNavigate,
      },
    }))
  }

  const onCompletedClicked = () => {
    if (isDataCollectionRunning) {
      setExitIntent('complete')
      setShowDataCollectionExitModal(true)
      return
    }
    onCompleted()
  }

  const stopDataCollectionAndContinue = async () => {
    setIsStoppingDataCollectionOnExit(true)
    try {
      await tauriStopDataCollection()
      await useUploadStatusStore.getState().refresh(activeCompany.id, protocolId)
    } catch (e) {
      console.error('[ProtocolRecorder] failed to stop data collection before continuing', e)
    } finally {
      setIsStoppingDataCollectionOnExit(false)
      setShowDataCollectionExitModal(false)
    }

    if (exitIntent === 'navigate') {
      navigate('/sessions/visit')
      return
    }
    onCompleted()
  }
  const onStepSelected = (id: TProtocolStepId) => {
    const index = orderedSteps.findIndex((step) => step.id === id)
    if (index !== -1) {
      setActiveStepIndex(index)
    } else {
      console.error(`Step with id ${id} not found in orderedSteps ${JSON.stringify(orderedSteps)}`)
    }
  }

  const anyStepUploadsActive = !!orderedSteps.find((step) => Object.keys(step.transient?.fileData?.uploadProgress || {}).length > 0)
  const protocolNotComplete = !orderedSteps.reduceRight((pv, v) => pv && v?.status !== IProtocolStepStatus.INCOMPLETE, true) || anyStepUploadsActive

  if (orderedSteps.length <= 0) {
    return <LoadingOverlay />
  }

  const getActiveStepViewId = () => {
    return groupsByStepId[orderedSteps[activeStepIndex].id]?.id || orderedSteps[activeStepIndex].id
  }

  // if a group step is in slot 0 we sometimes initialize focus mode
  // if (focusMode && orderedSteps.length > 0 && orderedSteps[activeStepIndex].kind === IProtocolStepKind.GROUP) {
  //     onNext()
  // }

  return (
    <Box
      className={protocolClasses.root}
      style={{ "--event-recorder-height": `${eventRecorderHeight}px` } as React.CSSProperties}>
      <Box className={cx(protocolClasses.protocolToolbar)}>
        <Button size="xs" data-testid="btn-back"
          onClick={onClickBack}><IconArrowLeft size={"2em"} /></Button>
        <Box style={{ display: "grid", grid: "1fr / auto 1fr", columnGap: "1em", paddingLeft: "3em" }}>
          <Box style={{ display: "grid", grid: "auto / auto auto", alignSelf: "center", columnGap: "0.25em" }}>
            <IconUser size={"1.5em"} />
            <Text> {subject?.name}</Text>
          </Box>
          <TextInput className={cx(protocolClasses.protocolName)} size="xs" ta="center"
            data-testid="input-protocol-name"
            fw={"bold"}
            defaultValue={selectedProtocol.name}
            onBlur={(ev) => {
              void coordinatorWriteProtocol(activeCompany.id, protocolId, {
                name: ev.currentTarget.value
              })
            }}
          />
        </Box>
        <PendingWritesTray />
        <Box>
          <Button size={"xs"} mr={"2rem"}
            onClick={() => viewMode === 'focus' ? setViewMode('normal') : enterFocusMode()}
            rightSection={
              viewMode === 'focus' ? <IconPlayerStop /> :
                <IconPlayerPlay size={"1rem"} />
            }>
            Start Session
          </Button>
        </Box>
        <Popover width={300} position={"bottom"} withArrow shadow={"md"}>
          <Popover.Target>
            <Button size={"xs"}><IconTimezone size="1.5em" /></Button>
          </Popover.Target>
          <Popover.Dropdown>
            <div className={cx(protocolClasses.protocolSettings)}>
              <TimezoneSelector selectedZone={timezone} onChange={setTimezone} />
              <Button size={"xs"} onClick={openProtocolInstructions}>
                Protocol Instructions
              </Button>
            </div>
          </Popover.Dropdown>
        </Popover>
      </Box>
      <Box className={protocolClasses.content}>
        {viewMode === 'normal' && (
          <div className={cx(protocolClasses.sidePanel)}>

            <div className={cx("header")}>
              <Box></Box>
              <Text size="sm">Step name</Text>
              <Text size="sm" mr={"0.25em"}>Status</Text>
            </div>
            <StepsView protocol={selectedProtocol}
              onStepSelection={onStepSelected}
              activeStep={getActiveStepViewId()}
              subjectId={subjectId}
              onStepChange={onStepChanged}
            />
            <div className={protocolClasses.stepsActionBar}>
              <Box />
              <Button size={"compact-xs"} data-testid="btn-protocol-complete" onClick={onCompletedClicked}
                disabled={protocolNotComplete}>Complete</Button>
            </div>
          </div>
        )}
        {viewMode === 'normal' ? renderNormalContent() : renderFocusContent()}
      </Box>
      {viewMode === 'normal' && countDownState.counting && (
        <FloatingCountdownPanel
          countDownState={countDownState}
          onStop={stopCountdown}
          onCancel={cancelCountdown}
        />
      )}
      <Box
        className={protocolClasses.eventRecorderContainer}
        ref={eventRecorderRef}>
        {isEventRecorderExpanded ? (
          <Box className={protocolClasses.eventRecorderExpanded}>
            <Box className={protocolClasses.collapseButton}
              onClick={() => setIsEventRecorderExpanded(false)}>
              <Text size={"xs"}>Protocol Events</Text>
              <IconChevronDown size="1em" />
            </Box>
            <EventRecorder
              key={`event-recorder-${formResetEpoch}`}
              subjectId={subjectId}
              protocolId={protocolId}
              stepId={undefined}
              timezone={timezone}
              autoStart={true}
              createEvent={createEvent}
            />
            <Box className={protocolClasses.eventRecorderExpanded}>
              <EventListView
                key={`event-list-${formResetEpoch}`}
                timezone={timezone}
                events={protocolEvents}
                onDeleteEvent={onDeleteEvent}
                readonly={true}
                canDelete={true}
              />
            </Box>

          </Box>
        ) : (
          <Box className={protocolClasses.eventRecorderBar} onClick={() => setIsEventRecorderExpanded(true)}>
            <Box className={protocolClasses.collapseButton}>
              <Text size={"xs"}>Protocol Events</Text>
              <IconChevronUp size="1em" />
            </Box>
            <Box />
          </Box>
        )}
      </Box>
      <PendingActivityModal
        companyId={activeCompany.id}
        protocolId={protocolId}
        onCompleteProtocol={onCompletedClicked}
      />
      <Modal
        withinPortal={true}
        opened={showDataCollectionExitModal}
        onClose={() => {
          if (isStoppingDataCollectionOnExit) return
          setShowDataCollectionExitModal(false)
        }}
        title={<Text fw="bold" size="1em" ta="center">Stop data collection?</Text>}
        size="md"
        data-testid="data-collection-exit-modal"
      >
        <Box style={{ display: 'grid', grid: 'auto auto / 1fr', rowGap: '0.75rem' }}>
          <Text size="sm">
            Data collection is still running. Would you like to stop data collection and proceed?
          </Text>
          <Button
            size="xs"
            onClick={() => { void stopDataCollectionAndContinue() }}
            disabled={isStoppingDataCollectionOnExit}
            data-testid="btn-stop-data-collection-and-continue"
          >
            {isStoppingDataCollectionOnExit ? 'Stopping...' : 'Stop data collection and continue'}
          </Button>
        </Box>
      </Modal>
    </Box>
  )
}
  ;
